import {UNIT_MS} from './duration';

/** The timezone the business operates in — calendar boundaries ("this week") are resolved here. */
export const BUSINESS_TIME_ZONE = 'Asia/Jerusalem';

/**
 * Returns the start of the week (Sunday 00:00 wall-clock in `timeZone`) containing `date`.
 * Defaults to {@link BUSINESS_TIME_ZONE} so every "this week" cutoff agrees, regardless of the
 * host's or UTC's idea of the day.
 */
export function getStartOfWeek(date: Date, timeZone: string = BUSINESS_TIME_ZONE): Date {
  const {year, month, day} = getDatePartsInTimeZone(date, timeZone);
  const weekday = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).getUTCDay();
  const sunday = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) - weekday));
  return startOfDayInTimeZone(sunday.toISOString().slice(0, 10), timeZone);
}

/**
 * Returns a new Date offset `days` later than the given date.
 */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * UNIT_MS.d!);
}

/**
 * Returns a new Date offset `days` earlier than the given date.
 */
export function subtractDays(date: Date, days: number): Date {
  return new Date(date.getTime() - days * UNIT_MS.d!);
}

/**
 * Returns a new Date offset `hours` earlier than the given date.
 */
export function subtractHours(date: Date, hours: number): Date {
  return new Date(date.getTime() - hours * UNIT_MS.h!);
}

/**
 * Returns a new Date offset `years` earlier than the given date.
 */
export function subtractYears(date: Date, years: number): Date {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() - years);
  return result;
}

/**
 * Returns whichever of two dates is later. Returns `a` when they are equal.
 */
export function laterDate(a: Date, b: Date): Date {
  return b.getTime() > a.getTime() ? b : a;
}

/**
 * Converts a Date to a YYYY-MM-DD string using local time.
 */
export function dateToIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parses a YYYY-MM-DD string into a local Date, avoiding UTC timezone offset issues.
 */
export function isoToDate(iso: string): Date {
  const parts = iso.split('-').map(Number);
  return new Date(parts[0] ?? 0, (parts[1] ?? 1) - 1, parts[2] ?? 1);
}

/**
 * Returns true when the given date falls on a later calendar day than today (local time).
 * Accepts a `YYYY-MM-DD` string (parsed via `isoToDate` to avoid UTC offset issues) or a `Date`.
 */
export function isFutureDate(value: Date | string): boolean {
  const date = value instanceof Date ? value : isoToDate(value);
  return getDaysAgo(date) < 0;
}

/**
 * Safely converts a Date, date string, null, or undefined to an ISO string.
 * Returns an empty string if the value is nullish or results in an invalid date.
 */
export function toISO(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value as string | Date);
  return isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Convert Unix timestamp to ISO date string
 * @param timestamp - Unix timestamp (seconds since epoch)
 * @returns ISO date string
 */
export function timestampToISO(timestamp: number | string): string {
  const timestampNum = typeof timestamp === 'string' ? Number(timestamp) : timestamp;
  return new Date(timestampNum * 1000).toISOString();
}

/**
 * Get current date as ISO string
 * @returns Current date in ISO format
 */
export function nowISO(): string {
  return new Date().toISOString();
}

/**
 * Get the current calendar year
 * @returns Current year, e.g. 2026
 */
export function getCurrentYear(): number {
  return new Date().getFullYear();
}

/**
 * Formats a date to DD/MM/YYYY format
 * @param date - Date string or Date object to format
 * @returns Formatted date string in DD/MM/YYYY format
 */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-GB');
}

/** Maps app language codes to explicit regional locales for consistent date formatting. */
const LOCALE_MAP: Record<string, string> = {
  en: 'en-GB',
  he: 'he-IL'
};

/**
 * Formats a date using the given locale with day, short month, and year.
 * e.g. 'he' → "01 באוק׳ 2023", 'en' → "01 Oct 2023"
 * @param date - Date string or Date object to format
 * @param locale - App language code (e.g. 'he', 'en') or BCP 47 locale
 * @returns Formatted date string
 */
export function formatDateLocalized(date: string | Date, locale: string): string {
  const d = date instanceof Date ? date : new Date(date);
  const resolved = LOCALE_MAP[locale] ?? locale;
  return d.toLocaleDateString(resolved, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Formats a date with short month, year, and time using the given locale.
 * e.g. 'he' → "1 באפר׳ 2026, 10:30", 'en' → "1 Apr 2026, 10:30"
 * @param date - Date string or Date object to format
 * @param locale - App language code (e.g. 'he', 'en') or BCP 47 locale
 * @returns Formatted date and time string
 */
export function formatDateTimeLocalized(date: string | Date, locale: string): string {
  const d = date instanceof Date ? date : new Date(date);
  const resolved = LOCALE_MAP[locale] ?? locale;
  return new Intl.DateTimeFormat(resolved, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
}

/**
 * Formats a date with short month and time, without the year, using the given locale.
 * e.g. 'he' → "6 באוק׳, 22:14", 'en' → "6 Oct, 22:14"
 * @param date - Date string or Date object to format
 * @param locale - App language code (e.g. 'he', 'en') or BCP 47 locale
 * @returns Formatted day, month and time string
 */
export function formatDayMonthTimeLocalized(date: string | Date, locale: string): string {
  const d = date instanceof Date ? date : new Date(date);
  const resolved = LOCALE_MAP[locale] ?? locale;
  return new Intl.DateTimeFormat(resolved, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(d);
}

/**
 * Formats a date to DD/MM/YYYY HH:MM format
 * @param date - Date string or Date object to format
 * @returns Formatted date string with time in DD/MM/YYYY HH:MM format
 */
export function formatDateTime(date: string | Date): string {
  const d = new Date(date);
  const datePart = d.toLocaleDateString('en-GB');
  const timePart = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${datePart} ${timePart}`;
}

/**
 * Formats a date to HH:mm (24-hour) format
 * @param date - Date string or Date object to format
 * @returns Time string in HH:mm format
 */
export function formatTime(date: string | Date): string {
  return new Date(date).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Formats a date as a localized relative time label using the given locale.
 * e.g. 'he' → "לפני 5 דקות", 'en' → "5 minutes ago"
 * @param date - Date string or Date object to compare against now
 * @param locale - App language code (e.g. 'he', 'en') or BCP 47 locale
 * @returns Human-readable relative time string
 */
export function formatRelativeTimeLocalized(date: string | Date, locale: string): string {
  const resolved = LOCALE_MAP[locale] ?? locale;
  const rtf = new Intl.RelativeTimeFormat(resolved, {numeric: 'auto'});
  const diffMins = Math.round((new Date(date).getTime() - Date.now()) / 60_000);
  if (diffMins === 0) return rtf.format(0, 'second');
  if (Math.abs(diffMins) < 60) return rtf.format(diffMins, 'minute');
  const diffHours = Math.round(diffMins / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, 'hour');
  return rtf.format(Math.round(diffHours / 24), 'day');
}

/**
 * Returns the number of calendar days between `date` and local now, where 0 = today,
 * 1 = yesterday, 2 = the day before yesterday, and so on. Negative values mean a future date.
 * Compares calendar days (midnight to midnight), not 24-hour spans.
 */
export function getDaysAgo(date: Date): number {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(new Date()) - startOfDay(date)) / msPerDay);
}

/**
 * Formats a date as a localized relative-day label based on calendar-day difference from now.
 * e.g. 'en' → "Today"/"Yesterday"/"2 days ago", 'he' → "היום"/"אתמול"/"שלשום"/"לפני 3 ימים".
 * Unlike formatRelativeTimeLocalized, this buckets by calendar day, not elapsed hours.
 */
export function formatRelativeDayLocalized(date: Date, locale: string): string {
  const resolved = LOCALE_MAP[locale] ?? locale;
  const rtf = new Intl.RelativeTimeFormat(resolved, {numeric: 'auto'});
  return rtf.format(-getDaysAgo(date), 'day');
}

/**
 * Formats an instant as a full Hebrew date and time (e.g. "יום שלישי, 2 ביוני 2026 בשעה 17:00") in
 * a specific IANA timezone. Used in email templates to show expiry deadlines to buyers and lawyers.
 */
export function formatHebrewDateTime(date: Date, timeZone: string = BUSINESS_TIME_ZONE): string {
  return new Intl.DateTimeFormat('he-IL', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(date);
}

/**
 * Returns the year/month/day of a date as rendered in a specific IANA timezone — not the host
 * process's timezone. Use when a date must be computed consistently regardless of where the
 * code runs (e.g. a report that must use Israel's calendar day even when run from a UTC server).
 */
export function getDatePartsInTimeZone(
  date: Date,
  timeZone: string
): {year: string; month: string; day: string} {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return {year: get('year'), month: get('month'), day: get('day')};
}

/**
 * Formats a date as DD/MM/YYYY in a specific IANA timezone — the timezone-explicit counterpart
 * to {@link formatDate}, which uses the host process's timezone.
 */
export function formatDateInTimeZone(date: Date, timeZone: string): string {
  const {year, month, day} = getDatePartsInTimeZone(date, timeZone);
  return `${day}/${month}/${year}`;
}

/**
 * Formats a date as HH:MM:SS (24-hour) in a specific IANA timezone.
 */
export function formatTimeInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('hour')}:${get('minute')}:${get('second')}`;
}

/**
 * Formats the weekday name (e.g. "יום ראשון" / "Sunday") of an instant as it falls in a specific
 * IANA timezone.
 */
export function formatWeekdayInTimeZone(date: Date, timeZone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {timeZone, weekday: 'long'}).format(date);
}

/** Formats a `YYYY-MM-DD` calendar date as D.M (e.g. "18.10") — timezone-independent. */
export function formatIsoDayMonth(isoDate: string): string {
  const [, month = '', day = ''] = isoDate.split('-');
  return `${Number(day)}.${Number(month)}`;
}

/** Formats an instant as D.M (e.g. "18.10") in a specific IANA timezone. */
export function formatDayMonthInTimeZone(date: Date, timeZone: string): string {
  const {month, day} = getDatePartsInTimeZone(date, timeZone);
  return `${Number(day)}.${Number(month)}`;
}

/** The UTC-vs-local offset (in ms) such that `instant + offset` reads as `timeZone`'s wall clock. */
function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );
  return asUtc - instant.getTime();
}

/**
 * Returns the UTC instant for a wall-clock time (`hour`:`minute`) on a `YYYY-MM-DD` calendar date
 * in a specific IANA timezone — DST-aware.
 */
export function wallClockToInstant(
  isoDate: string,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  const utcGuess = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1, hour, minute));
  // One correction pass isn't enough right at a DST transition (the offset itself can differ
  // between the guess and the corrected instant), so re-derive the offset from the corrected
  // instant and apply it again.
  const firstPass = new Date(utcGuess.getTime() - timeZoneOffsetMs(utcGuess, timeZone));
  return new Date(utcGuess.getTime() - timeZoneOffsetMs(firstPass, timeZone));
}

/**
 * Returns the UTC instant for local midnight (00:00:00.000) of a `YYYY-MM-DD` calendar date in a
 * specific IANA timezone — DST-aware. Use for date-range query boundaries that must align with a
 * fixed timezone's calendar day: `new Date('2026-08-01')` is UTC midnight, not Israel midnight,
 * which silently shifts the window by 2-3 hours depending on daylight saving.
 */
export function startOfDayInTimeZone(isoDate: string, timeZone: string): Date {
  return wallClockToInstant(isoDate, 0, 0, timeZone);
}

/**
 * Returns the calendar date, weekday (0 = Sunday) and minutes since midnight of an instant as
 * rendered in a specific IANA timezone — not the host process's timezone.
 */
export function getWallClockInTimeZone(
  date: Date,
  timeZone: string
): {isoDate: string; weekday: number; minutes: number} {
  const {year, month, day} = getDatePartsInTimeZone(date, timeZone);
  const [hh = '0', mm = '0'] = formatTimeInTimeZone(date, timeZone).split(':');
  const isoDate = `${year}-${month}-${day}`;
  return {isoDate, weekday: getIsoWeekday(isoDate), minutes: Number(hh) * 60 + Number(mm)};
}

/**
 * True when the wall-clock hour in `timeZone` is at or after `startHour` and before `endHour`
 * (e.g. 9 and 20 for 09:00–19:59).
 */
export function isWithinWallClockHours(
  date: Date,
  startHour: number,
  endHour: number,
  timeZone: string = BUSINESS_TIME_ZONE
): boolean {
  const {minutes} = getWallClockInTimeZone(date, timeZone);
  return minutes >= startHour * 60 && minutes < endHour * 60;
}

/** Weekday (0 = Sunday) of a `YYYY-MM-DD` calendar date — timezone-independent. */
export function getIsoWeekday(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCDay();
}

/** Adds (or subtracts) whole calendar days to a `YYYY-MM-DD` date — timezone-independent. */
export function addDaysToIsoDate(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Formats an upload timestamp for display.
 * Returns justNowLabel when the date is less than one minute ago,
 * otherwise returns "{uploadedAtPrefix} {date} {time}" with a LTR mark before the
 * date to ensure correct ordering in RTL contexts.
 * @param date - Date string or Date object of the upload
 * @param justNowLabel - Label for recent uploads, e.g. "Uploaded now" / "הועלה עכשיו"
 * @param uploadedAtPrefix - Prefix for older uploads, e.g. "uploaded at:" / "הועלה ב:"
 */
export function formatUploadedAt(
  date: string | Date | null | undefined,
  justNowLabel: string,
  uploadedAtPrefix: string
): string | undefined {
  if (date == null) return undefined;
  const d = new Date(date as string | Date);
  if (isNaN(d.getTime())) return undefined;
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return justNowLabel;
  // \u200E (Left-to-Right Mark) ensures the date/time numbers render in correct
  // order when displayed inside an RTL paragraph.
  return `${uploadedAtPrefix} \u200E${formatDateTime(d)}`;
}

const BUSINESS_START_HOUR = 9;
const BUSINESS_END_HOUR = 18;
const LAST_WORKING_WEEKDAY = 4; // Thursday; weekdays are 0 = Sunday

export const BUSINESS_DAY_MINUTES = (BUSINESS_END_HOUR - BUSINESS_START_HOUR) * 60;

/**
 * Minutes between two instants that fall inside business hours — Sunday to Thursday,
 * 09:00–18:00 wall-clock in `timeZone`. Evenings, Fridays and Saturdays don't count, so work
 * submitted outside hours starts counting at the next working day's opening. Returns 0 when
 * `to` is not after `from`.
 */
export function getBusinessMinutesBetween(
  from: Date,
  to: Date,
  timeZone: string = BUSINESS_TIME_ZONE
): number {
  if (to.getTime() <= from.getTime()) return 0;

  const atHour = (isoDate: string, hour: number) =>
    wallClockToInstant(isoDate, hour, 0, timeZone).getTime();
  const firstDate = getWallClockInTimeZone(from, timeZone).isoDate;
  const lastDate = getWallClockInTimeZone(to, timeZone).isoDate;

  let totalMs = 0;
  for (let isoDate = firstDate; isoDate <= lastDate; isoDate = addDaysToIsoDate(isoDate, 1)) {
    if (getIsoWeekday(isoDate) > LAST_WORKING_WEEKDAY) continue;

    // A day strictly between the two is worked in full — skip the timezone conversion for it.
    if (isoDate !== firstDate && isoDate !== lastDate) {
      totalMs += BUSINESS_DAY_MINUTES * 60_000;
      continue;
    }

    const start = Math.max(atHour(isoDate, BUSINESS_START_HOUR), from.getTime());
    const end = Math.min(atHour(isoDate, BUSINESS_END_HOUR), to.getTime());
    totalMs += Math.max(0, end - start);
  }
  return totalMs / 60_000;
}
